import React from 'react';

const DEFAULT_SKIP_LINK_LABEL = 'Aller au contenu';

/**
 * Invisible jusqu'au focus, puis `fixed` — la racine du layout a
 * `overflow-hidden` : un `absolute` au focus serait coupe (mesure Phase 0
 * MASTER-SKIPLINK-LANG, 2026-08-31). `sr-only` + `focus:not-sr-only` sans
 * `focus:fixed` = un lien que le clavier atteint et que personne ne voit.
 */
export const GS_SKIP_LINK_CLASS =
  'sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 ' +
  'focus:px-4 focus:py-2 focus:rounded-xl focus:bg-white focus:text-slate-900 ' +
  'focus:shadow-xs focus:outline-solid focus:outline-2 focus:outline-offset-2 ' +
  'focus:outline-brand-500 dark:focus:bg-slate-800 dark:focus:text-slate-100';

export interface GsLayoutProps {
  header: React.ReactNode;
  sidebar?: React.ReactNode;
  mobileNav?: React.ReactNode;
  children: React.ReactNode;
  /**
   * `id` pose sur le `<main>` — CIBLE du lien d'evitement (« Aller au contenu »).
   *
   * V3, decision A3 (2026-08-28) : l'ancre appartient au layout PARTAGE. Avant
   * ce lot, `<main>` ne portait ni `id` ni prop pour en poser un, donc la cible
   * ne pouvait pas venir de l'app ; le contournement (envelopper les `Routes`
   * dans un `<div id tabIndex={-1}>`) a ete explicitement REFUSE — il place
   * l'ancre A L'INTERIEUR de `<main>` et laisse chaque app reinventer la sienne.
   *
   * V9 / G6 (2026-08-31) : quand `mainId` est fourni, le layout pose AUSSI le
   * lien d'evitement en premier enfant (avant sidebar/header), invisible jusqu'au
   * focus. Libelle par prop, defaut FR — JAMAIS de t() dans @gs/ui.
   *
   * Quand l'id est fourni, `<main>` recoit aussi `tabIndex={-1}` pour etre
   * focalisable par programme : sans lui, plusieurs navigateurs deplacent le
   * defilement sans deplacer le focus, et le lecteur d'ecran reprend la lecture
   * a l'en-tete — le lien parait alors marcher pour un voyant et pas pour un
   * utilisateur de lecteur d'ecran.
   */
  mainId?: string;
  /** Libelle du lien d'evitement. Defaut FR ; l'app i18n passe t(...). */
  skipLinkLabel?: string;
}

export function GsLayout({
  header,
  sidebar,
  mobileNav,
  children,
  mainId,
  skipLinkLabel = DEFAULT_SKIP_LINK_LABEL,
}: GsLayoutProps) {
  const mainPadBottom = mobileNav ? 'pb-20 md:pb-0' : '';

  const focusMain = () => {
    if (!mainId) return;
    document.getElementById(mainId)?.focus();
  };

  return (
    // Hauteur d ecran MOINS le bandeau de preproduction (GS_TOP_BANNER_HEIGHT_VAR, publiee par
    // GsPreproductionBanner, absente = 0) : le bandeau pousse la page au lieu de la faire deborder.
    <div className="flex h-[calc(100vh-var(--gs-top-banner-height,0px))] overflow-hidden bg-white dark:bg-slate-900">
      {mainId ? (
        <a href={`#${mainId}`} className={GS_SKIP_LINK_CLASS} onClick={focusMain}>
          {skipLinkLabel}
        </a>
      ) : null}
      {sidebar}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {header}
        {/* Canvas de page = token bg-page (#fbfbfb, plus pâle que slate-50 — cartes bg-white respirent) — dark:bg-slate-900. MASTER-DS-STANDARD-VISUEL-GLOBAL 2026-06-17 (succède à MASTER-DS-POLISH-PASS qui posait slate-50). */}
        <main
          id={mainId}
          tabIndex={mainId ? -1 : undefined}
          className={`min-h-0 flex-1 overflow-auto bg-page dark:bg-slate-900 px-4 py-5 sm:px-6 sm:py-6 ${mainPadBottom}`}
        >
          {children}
        </main>
      </div>
      {/* mobileNav : composant autonome (fixed, md:hidden, safe area) — GsMobileBottomNav */}
      {mobileNav}
    </div>
  );
}
